import type { Editor as TEditor } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { NodeSelection, TextSelection } from "@tiptap/pm/state";
import { CellSelection, TableMap } from "@tiptap/pm/tables";
import {
  AlignVerticalJustifyCenter, AlignVerticalJustifyEnd, AlignVerticalJustifyStart, ChevronDown, ChevronRight, Columns2, Copy, GripVertical, Merge, MoveHorizontal,
  PaintBucket, RotateCcw, Split, Trash2,
} from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Popover, type Anchor } from "../Menu";
import { CELL_BACKGROUNDS } from "./nodes";

/*
 * Evernote's table controls, shown while the cursor is in a table:
 *   - a bar above the columns and one beside the rows: click a segment to select that column/row (and open
 *     its menu); drag a selected segment to move it;
 *   - "+" dots on every boundary of those bars: click to insert a column/row there;
 *   - a grip at the corner: click selects the table, drag moves it;
 *   - a menu button in the active cell: cell background, reset style, align content, table width, merge/split,
 *     copy, delete.
 * Everything is drawn in a fixed overlay over the editor and follows the table as it scrolls.
 */

interface Geo {
  pos: number; // table node position
  node: PMNode;
  map: TableMap;
  table: DOMRect;
  clip: DOMRect; // visible part of the editor scroll area
  cols: { left: number; right: number }[];
  rows: { top: number; bottom: number }[];
  cell: DOMRect | null; // where the menu button sits
  sel: { left: number; right: number; top: number; bottom: number } | null; // selected cell rect (map indexes)
  kind: "cell" | "cells" | "col" | "row" | "table";
}

function findTable(editor: TEditor): { pos: number; node: PMNode } | null {
  const { selection } = editor.state;
  if (selection instanceof NodeSelection && selection.node.type.name === "table") return { pos: selection.from, node: selection.node };
  const $p = selection instanceof CellSelection ? selection.$anchorCell : selection.$from;
  for (let d = $p.depth; d > 0; d--) {
    const n = $p.node(d);
    if (n.type.name === "table") return { pos: $p.before(d), node: n };
  }
  return null;
}

function measure(editor: TEditor, keep: boolean): Geo | null {
  // Only while editing (Evernote), or while one of the table's menus is open.
  if (editor.isDestroyed || !editor.isEditable || (!keep && !editor.view.hasFocus())) return null;
  const found = findTable(editor);
  if (!found) return null;
  const { pos, node } = found;
  const view = editor.view;
  const wrapper = view.nodeDOM(pos) as HTMLElement | null;
  const tableEl = wrapper?.querySelector("table");
  const scroller = wrapper?.closest(".editor-scroll") as HTMLElement | null;
  if (!wrapper || !tableEl || !scroller) return null;
  const map = TableMap.get(node);
  const start = pos + 1;
  const table = tableEl.getBoundingClientRect();
  const cellRect = (cellPos: number) => (view.nodeDOM(start + cellPos) as HTMLElement | null)?.getBoundingClientRect() ?? null;
  const cols: Geo["cols"] = [];
  for (let c = 0; c < map.width; c++) {
    let r: DOMRect | null = null;
    for (let row = 0; row < map.height && !r; row++) {
      const cp = map.map[row * map.width + c]!;
      const cell = node.nodeAt(cp);
      if (cell && cell.attrs.colspan === 1) r = cellRect(cp);
    }
    if (r) cols.push({ left: r.left, right: r.right });
    else {
      const w = table.width / map.width;
      cols.push({ left: table.left + c * w, right: table.left + (c + 1) * w });
    }
  }
  const rows = Array.from(tableEl.rows, (tr) => {
    const r = tr.getBoundingClientRect();
    return { top: r.top, bottom: r.bottom };
  });
  const sc = scroller.getBoundingClientRect();
  const wr = wrapper.getBoundingClientRect();
  const clip = new DOMRect(Math.max(sc.left, wr.left - 40), sc.top, Math.min(sc.right, wr.right + 8) - Math.max(sc.left, wr.left - 40), sc.height);

  const { selection } = editor.state;
  let sel: Geo["sel"] = null;
  let kind: Geo["kind"] = "cell";
  let cell: DOMRect | null = null;
  if (selection instanceof CellSelection) {
    const a = selection.$anchorCell.pos - start;
    const h = selection.$headCell.pos - start;
    sel = map.rectBetween(a, h);
    const allCols = sel.left === 0 && sel.right === map.width;
    const allRows = sel.top === 0 && sel.bottom === map.height;
    kind = allCols && allRows ? "table" : allRows ? "col" : allCols ? "row" : "cells";
    // One (possibly merged) cell, e.g. right after "Merge cells".
    if (kind === "cells" && map.cellsInRect(sel).length === 1) kind = "cell";
    const topRight = map.map[sel.top * map.width + sel.right - 1]!;
    cell = cellRect(topRight);
  } else if (selection instanceof NodeSelection) {
    kind = "table";
    sel = { left: 0, right: map.width, top: 0, bottom: map.height };
    cell = cellRect(map.map[map.width - 1]!);
  } else {
    const $c = selection.$from;
    for (let d = $c.depth; d > 0; d--) {
      const role = $c.node(d).type.spec.tableRole;
      if (role === "cell" || role === "header_cell") {
        const cp = $c.before(d) - start;
        cell = cellRect(cp);
        const r = map.findCell(cp);
        sel = r;
        break;
      }
    }
  }
  return { pos, node, map, table, clip, cols, rows, cell, sel, kind };
}

export function TableControls({ editor }: { editor: TEditor }) {
  const [geo, setGeo] = useState<Geo | null>(null);
  const [menu, setMenu] = useState<Anchor | null>(null);
  const raf = useRef(0);
  const menuOpen = useRef(false);
  menuOpen.current = !!menu;
  const update = useCallback(() => {
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => setGeo(measure(editor, menuOpen.current)));
  }, [editor]);

  useEffect(() => {
    editor.on("transaction", update);
    editor.on("focus", update);
    editor.on("blur", update);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    update();
    return () => {
      editor.off("transaction", update);
      editor.off("focus", update);
      editor.off("blur", update);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      cancelAnimationFrame(raf.current);
    };
  }, [editor, update]);
  // Table cells resize as their text wraps; keep the bars on them.
  useLayoutEffect(() => {
    if (!geo) return;
    const el = (editor.view.nodeDOM(geo.pos) as HTMLElement | null)?.querySelector("table");
    if (!el) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [editor, geo?.pos, update]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!geo) return null;
  const { clip, table, cols, rows, map, sel, kind, pos } = geo;
  const BAR = 12; // bar thickness
  const GAP = 8; // between bar and table
  const top = table.top - clip.top;
  const left = table.left - clip.left;
  const barTop = top - GAP - BAR;
  const barLeft = left - GAP - BAR;
  const start = pos + 1;

  const cellPosAt = (row: number, col: number) => start + map.map[row * map.width + col]!;
  const select = (anchor: number, head: number) => {
    const { state } = editor;
    editor.view.dispatch(state.tr.setSelection(CellSelection.create(state.doc, anchor, head)));
    editor.view.focus();
    // Measure now, so the menu that opens next already has the new selection's items.
    setGeo(measure(editor, true));
  };
  const selectCol = (c: number, e: React.MouseEvent) => {
    select(cellPosAt(0, c), cellPosAt(map.height - 1, c));
    setMenu(e.currentTarget as HTMLElement);
  };
  const selectRow = (r: number, e: React.MouseEvent) => {
    select(cellPosAt(r, 0), cellPosAt(r, map.width - 1));
    setMenu(e.currentTarget as HTMLElement);
  };
  const insert = (axis: "col" | "row", at: number) => {
    // Put the cursor in a cell next to the boundary, then add before/after it.
    const last = axis === "col" ? map.width : map.height;
    const idx = Math.min(at, last - 1);
    const cp = axis === "col" ? cellPosAt(0, idx) : cellPosAt(idx, 0);
    const c = editor.chain().focus().setTextSelection(cp + 2);
    if (axis === "col") (at < last ? c.addColumnBefore() : c.addColumnAfter()).run();
    else (at < last ? c.addRowBefore() : c.addRowAfter()).run();
    // The caret goes into the new column/row, ready to type (Evernote).
    const t = editor.state.doc.nodeAt(pos);
    if (!t) return;
    const m = TableMap.get(t);
    const cell = axis === "col" ? m.map[at]! : m.map[at * m.width]!;
    editor.chain().setTextSelection(pos + 1 + cell + 2).run();
  };

  // Only the bars and "+" dots near the visible part of the table are drawn: a 208-row table drew 420 of them on
  // every keystroke in a cell.
  const near = (a: number, b: number, lo: number, hi: number) => b >= lo - 120 && a <= hi + 120;
  const rowNear = (top: number, bottom: number) => near(top, bottom, clip.top, clip.bottom);
  const colNear = (left: number, right: number) => near(left, right, clip.left, clip.right);

  const colSelected = (c: number) => !!sel && (kind === "col" || kind === "table") && c >= sel.left && c < sel.right;
  const rowSelected = (r: number) => !!sel && (kind === "row" || kind === "table") && r >= sel.top && r < sel.bottom;

  return createPortal(
    <div className="table-ctl" style={{ left: clip.left, top: clip.top, width: clip.width, height: clip.height }} onMouseDown={(e) => e.preventDefault()}>
      {/* Column bar */}
      {cols.map((c, i) => colNear(c.left, c.right) && (
        <DragSegment
          key={`c${i}`}
          className={`tc-seg col${colSelected(i) ? " on" : ""}`}
          style={{ left: c.left - clip.left + 1, top: barTop, width: c.right - c.left - 2, height: BAR }}
          draggable={colSelected(i) && kind === "col"}
          axis="x"
          onClick={(e) => selectCol(i, e)}
          onDrop={(x) => moveTo(editor, pos, "col", sel!, indexAt(cols.map((k) => [k.left, k.right]), x))}
        />
      ))}
      {[...cols.map((c) => c.left), cols.at(-1)!.right].map((x, i) => colNear(x, x) && (
        <button key={`ci${i}`} className="tc-ins" style={{ left: x - clip.left - 9, top: barTop + BAR / 2 - 9 }} title="Insert column" aria-label="Insert column" onClick={() => insert("col", i)}>
          <span />
        </button>
      ))}
      {/* Row bar */}
      {rows.map((r, i) => rowNear(r.top, r.bottom) && (
        <DragSegment
          key={`r${i}`}
          className={`tc-seg row${rowSelected(i) ? " on" : ""}`}
          style={{ left: barLeft, top: r.top - clip.top + 1, width: BAR, height: r.bottom - r.top - 2 }}
          draggable={rowSelected(i) && kind === "row"}
          axis="y"
          onClick={(e) => selectRow(i, e)}
          onDrop={(y) => moveTo(editor, pos, "row", sel!, indexAt(rows.map((k) => [k.top, k.bottom]), y))}
        />
      ))}
      {[...rows.map((r) => r.top), rows.at(-1)!.bottom].map((y, i) => rowNear(y, y) && (
        <button key={`ri${i}`} className="tc-ins" style={{ left: barLeft + BAR / 2 - 9, top: y - clip.top - 9 }} title="Insert row" aria-label="Insert row" onClick={() => insert("row", i)}>
          <span />
        </button>
      ))}
      {/* Corner grip: click selects the table, drag moves it */}
      <button
        className={`tc-grip${kind === "table" ? " on" : ""}`}
        style={{ left: barLeft - 2, top: barTop - 2 }}
        title="Select table (drag to move)"
        aria-label="Select table"
        draggable
        onClick={(e) => {
          select(cellPosAt(0, 0), cellPosAt(map.height - 1, map.width - 1));
          setMenu(e.currentTarget);
        }}
        onDragStart={(e) => {
          const { state } = editor;
          const ns = NodeSelection.create(state.doc, pos);
          editor.view.dispatch(state.tr.setSelection(ns));
          const slice = ns.content();
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/html", (editor.view.nodeDOM(pos) as HTMLElement).outerHTML);
          e.dataTransfer.setDragImage(editor.view.nodeDOM(pos) as HTMLElement, 0, 0);
          editor.view.dragging = { slice, move: true, node: ns } as typeof editor.view.dragging;
        }}
      >
        <GripVertical size={13} />
      </button>
      {/* Selection outline and the cell menu button */}
      {sel && geo.cell && (
        <button
          className="tc-cellbtn"
          style={{ left: geo.cell.right - clip.left - 24, top: geo.cell.top - clip.top + 4 }}
          title="More options"
          aria-label="More options"
          onClick={(e) => setMenu(e.currentTarget)}
        >
          <ChevronDown size={12} />
        </button>
      )}
      {menu && (
        <CellMenu
          editor={editor}
          geo={geo}
          anchor={menu}
          onClose={() => {
            setMenu(null);
            menuOpen.current = false;
            update();
          }}
        />
      )}
    </div>,
    document.body,
  );
}

/** A bar segment: click to select; when its column/row is selected, drag it to a new place. */
function DragSegment({ className, style, draggable, axis, onClick, onDrop }: {
  className: string;
  style: React.CSSProperties;
  draggable: boolean;
  axis: "x" | "y";
  onClick: (e: React.MouseEvent) => void;
  onDrop: (at: number) => void;
}) {
  const drag = useRef<{ start: number; moved: boolean } | null>(null);
  const justDragged = useRef(false);
  const [ghost, setGhost] = useState<number | null>(null);
  return (
    <div
      className={`${className}${draggable ? " grab" : ""}`}
      style={{ ...style, ...(ghost != null && { transform: axis === "x" ? `translateX(${ghost}px)` : `translateY(${ghost}px)`, zIndex: 2 }) }}
      onPointerDown={(e) => {
        if (!draggable) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { start: axis === "x" ? e.clientX : e.clientY, moved: false };
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        const delta = (axis === "x" ? e.clientX : e.clientY) - d.start;
        if (Math.abs(delta) > 4) d.moved = true;
        if (d.moved) setGhost(delta);
      }}
      onPointerUp={(e) => {
        const d = drag.current;
        drag.current = null;
        setGhost(null);
        justDragged.current = !!d?.moved;
        if (d?.moved) onDrop(axis === "x" ? e.clientX : e.clientY);
      }}
      onClick={(e) => {
        if (justDragged.current) justDragged.current = false;
        else onClick(e);
      }}
    />
  );
}

function indexAt(spans: [number, number][], at: number): number {
  for (let i = 0; i < spans.length; i++) if (at < (spans[i]![0] + spans[i]![1]) / 2) return i;
  return spans.length;
}

/** Moves the selected columns/rows so they start at boundary `to` (tables without merged cells only). */
function moveTo(editor: TEditor, pos: number, axis: "col" | "row", sel: { left: number; right: number; top: number; bottom: number }, to: number) {
  const { state } = editor;
  const table = state.doc.nodeAt(pos);
  if (!table) return;
  let merged = false;
  table.descendants((n) => {
    if (n.attrs.colspan > 1 || n.attrs.rowspan > 1) merged = true;
    return !merged && n.type.name === "tableRow";
  });
  if (merged) return;
  const [from, until] = axis === "col" ? [sel.left, sel.right] : [sel.top, sel.bottom];
  if (to >= from && to <= until) return;
  const reorder = <T,>(items: T[]): T[] => {
    const moving = items.slice(from, until);
    const rest = [...items.slice(0, from), ...items.slice(until)];
    const at = to > until ? to - (until - from) : to;
    return [...rest.slice(0, at), ...moving, ...rest.slice(at)];
  };
  const rows: PMNode[] = [];
  table.forEach((r) => rows.push(r));
  const newRows = axis === "row" ? reorder(rows) : rows.map((r) => {
    const cells: PMNode[] = [];
    r.forEach((c) => cells.push(c));
    return r.copy(r.type.schema.nodes.tableRow!.createChecked(null, reorder(cells)).content);
  });
  const next = table.copy(table.type.schema.nodes.table!.createChecked(table.attrs, newRows).content);
  const tr = state.tr.replaceWith(pos, pos + table.nodeSize, next);
  // Keep the moved columns/rows selected.
  const map = TableMap.get(next);
  const at = to > until ? to - (until - from) : to;
  const n = until - from;
  const s = pos + 1;
  const [a, h] = axis === "col"
    ? [s + map.map[at]!, s + map.map[(map.height - 1) * map.width + at + n - 1]!]
    : [s + map.map[at * map.width]!, s + map.map[(at + n - 1) * map.width + map.width - 1]!];
  tr.setSelection(CellSelection.create(tr.doc, a, h));
  editor.view.dispatch(tr);
}

/** Writes per-column widths (px) into every cell's colwidth. */
function setWidths(editor: TEditor, pos: number, widths: number[], layout: string | null) {
  const { state } = editor;
  const table = state.doc.nodeAt(pos);
  if (!table) return;
  const map = TableMap.get(table);
  const tr = state.tr;
  const seen = new Set<number>();
  for (let i = 0; i < map.map.length; i++) {
    const cp = map.map[i]!;
    if (seen.has(cp)) continue;
    seen.add(cp);
    const cell = table.nodeAt(cp)!;
    const col = map.colCount(cp);
    const colwidth = Array.from({ length: cell.attrs.colspan as number }, (_, k) => Math.max(40, Math.round(widths[col + k]!)));
    tr.setNodeMarkup(pos + 1 + cp, null, { ...cell.attrs, colwidth });
  }
  tr.setNodeMarkup(pos, null, { ...table.attrs, layout });
  editor.view.dispatch(tr);
}

function CellMenu({ editor, geo, anchor, onClose }: { editor: TEditor; geo: Geo; anchor: Anchor; onClose: () => void }) {
  const [sub, setSub] = useState<null | { kind: "bg" | "align" | "width"; anchor: HTMLElement }>(null);
  const { kind, pos, cols, map } = geo;
  const c = () => editor.chain().focus();
  const done = (fn: () => void) => () => {
    fn();
    onClose();
  };
  const merged = (() => {
    if (kind !== "cell" || !geo.sel) return false;
    const cell = geo.node.nodeAt(map.map[geo.sel.top * map.width + geo.sel.left]!);
    return !!cell && (cell.attrs.colspan > 1 || cell.attrs.rowspan > 1);
  })();
  // prosemirror-tables' setCellAttribute skips the whole selection when its last cell already has the
  // value (so "Reset style" did nothing if that cell was plain); set it on every selected cell instead.
  const setAttrs = (attrs: Record<string, unknown>) => {
    const { state } = editor;
    const t = state.doc.nodeAt(pos);
    if (!geo.sel || t?.type.name !== "table") return;
    const tr = state.tr;
    for (const rel of TableMap.get(t).cellsInRect(geo.sel)) {
      const cell = tr.doc.nodeAt(pos + 1 + rel);
      if (cell && Object.entries(attrs).some(([k, v]) => cell.attrs[k] !== v)) tr.setNodeMarkup(pos + 1 + rel, null, { ...cell.attrs, ...attrs });
    }
    if (tr.docChanged) editor.view.dispatch(tr);
    editor.view.focus();
  };
  const copy = () => {
    const { state } = editor;
    if (!(state.selection instanceof CellSelection) && geo.sel) {
      const s = pos + 1;
      const a = s + map.map[geo.sel.top * map.width + geo.sel.left]!;
      editor.view.dispatch(state.tr.setSelection(CellSelection.create(state.doc, a, a)));
    }
    editor.view.focus();
    document.execCommand("copy");
  };
  // Targets leave 2px for the collapsed outer borders, so the table never gets a scrollbar.
  const noteWidth = () => (editor.view.dom as HTMLElement).clientWidth - 2;
  const windowWidth = () => {
    const sc = (editor.view.dom as HTMLElement).closest(".editor-scroll") as HTMLElement | null;
    return sc ? sc.clientWidth - 48 - 2 : noteWidth();
  };
  const current = cols.map((k) => k.right - k.left);
  const scaled = (target: number) => {
    const total = current.reduce((a, b) => a + b, 0) || 1;
    return current.map((w) => (w * target) / total);
  };
  const copyLabel = kind === "col" ? "Copy column" : kind === "row" ? "Copy row" : kind === "table" ? "Copy table" : kind === "cells" ? "Copy cells" : "Copy cell";
  const subItem = (label: string, icon: ReactNode, k: "bg" | "align" | "width", hint?: string) => (
    <button
      className={`menu-item${sub?.kind === k ? " active" : ""}`}
      onMouseEnter={(e) => setSub({ kind: k, anchor: e.currentTarget })}
      onClick={(e) => setSub({ kind: k, anchor: e.currentTarget })}
    >
      <span className="menu-icon">{icon}</span>
      <span className="menu-label">{label}</span>
      {hint && <span className="menu-hint">{hint}</span>}
      <ChevronRight size={13} className="menu-sub" />
    </button>
  );
  const item = (label: string, icon: ReactNode, fn: () => void, danger = false) => (
    <button className={`menu-item${danger ? " danger" : ""}`} onMouseEnter={() => setSub(null)} onClick={done(fn)}>
      <span className="menu-icon">{icon}</span>
      <span className="menu-label">{label}</span>
    </button>
  );
  const valign = (() => {
    const { $from } = editor.state.selection;
    for (let d = $from.depth; d > 0; d--) if ($from.node(d).type.spec.tableRole?.includes("cell")) return ($from.node(d).attrs.valign as string | null) ?? "top";
    return "top";
  })();
  return (
    <>
      <Popover anchor={anchor} onClose={() => (sub ? setSub(null) : onClose())} className="menu table-menu">
        {subItem("Cell background", <PaintBucket size={14} />, "bg")}
        {item("Reset style", <RotateCcw size={14} />, () => setAttrs({ background: null, valign: null }))}
        {subItem("Align content", <AlignVerticalJustifyStart size={14} />, "align", valign === "middle" ? "Center" : valign === "bottom" ? "Bottom" : "Top")}
        <div className="menu-sep" />
        {subItem("Set table width", <MoveHorizontal size={14} />, "width")}
        <div className="menu-sep" />
        {kind !== "cell" && item("Merge cells", <Merge size={14} />, () => c().mergeCells().run())}
        {merged && item("Split cell", <Split size={14} />, () => c().splitCell().run())}
        {item(copyLabel, <Copy size={14} />, copy)}
        {kind === "col" && item("Delete column", <Trash2 size={14} />, () => c().deleteColumn().run(), true)}
        {kind === "row" && item("Delete row", <Trash2 size={14} />, () => c().deleteRow().run(), true)}
        {kind === "table" && item("Delete table", <Trash2 size={14} />, () => c().deleteTable().run(), true)}
      </Popover>
      {sub && (
        <Popover anchor={sub.anchor} side="right" onClose={() => setSub(null)} className={`menu table-sub ${sub.kind}`}>
          {sub.kind === "bg" && (
            <div className="cell-swatches">
              <button className="cell-swatch none" title="No color" onClick={done(() => setAttrs({ background: null }))} />
              {CELL_BACKGROUNDS.map((k) => (
                <button key={k} className="cell-swatch" data-bg={k} title={k.replace("-1", " (soft)").replace("-2", "")} onClick={done(() => setAttrs({ background: k }))} />
              ))}
            </div>
          )}
          {sub.kind === "align" &&
            ([["top", "Top", AlignVerticalJustifyStart], ["middle", "Center", AlignVerticalJustifyCenter], ["bottom", "Bottom", AlignVerticalJustifyEnd]] as const).map(([v, label, Icon]) => (
              <button key={v} className={`menu-item${valign === v ? " active" : ""}`} onClick={done(() => setAttrs({ valign: v === "top" ? null : v }))}>
                <span className="menu-icon"><Icon size={14} /></span>
                <span className="menu-label">{label}</span>
              </button>
            ))}
          {sub.kind === "width" && (
            <>
              <button className="menu-item" onClick={done(() => setWidths(editor, pos, scaled(noteWidth()), null))}>
                <span className="menu-icon"><Columns2 size={14} /></span>
                <span className="menu-label">Set table as note width</span>
              </button>
              <button className="menu-item" onClick={done(() => setWidths(editor, pos, scaled(windowWidth()), "window"))}>
                <span className="menu-icon"><MoveHorizontal size={14} /></span>
                <span className="menu-label">Set table as window width</span>
              </button>
              <button className="menu-item" onClick={done(() => {
                const total = current.reduce((a, b) => a + b, 0);
                setWidths(editor, pos, current.map(() => total / current.length), geo.node.attrs.layout as string | null);
              })}>
                <span className="menu-icon"><Columns2 size={14} /></span>
                <span className="menu-label">Distribute columns evenly</span>
              </button>
            </>
          )}
        </Popover>
      )}
    </>
  );
}
